<template>
  <v-navigation-drawer :value="!!user" right fixed temporary width="480" @input="v => !v && $emit('close')">
    <div v-if="user" class="pa-4">
      <div class="d-flex align-center mb-3">
        <div>
          <div class="text-h6 font-weight-bold">{{ user.name }}</div>
          <div class="caption grey--text">{{ user.role || '—' }}<span v-if="user.roles && user.roles.length"> · {{ user.roles.join(', ') }}</span> · {{ user.level_name }}</div>
        </div>
        <v-spacer />
        <v-btn icon @click="$emit('close')"><v-icon>mdi-close</v-icon></v-btn>
      </div>

      <v-progress-linear v-if="loading" indeterminate />
      <v-alert v-else-if="error" type="error" dense outlined>{{ error }}
        <v-btn small text @click="load">Retry</v-btn>
      </v-alert>

      <div v-else-if="detail">
        <div class="subtitle-2 font-weight-bold mb-1">Score trend</div>
        <v-progress-linear v-if="trendLoading" indeterminate />
        <AdminTrendChart v-else :buckets="trend" class="mb-3" />

        <div class="d-flex align-center mb-4">
          <v-chip color="primary" dark class="mr-3">Score {{ detail.scores.growth_score }}</v-chip>
          <v-sparkline v-if="detail.daily_points && detail.daily_points.length > 1" :value="detail.daily_points.map(d => d.points)" color="primary" smooth auto-draw :line-width="2" height="40" />
        </div>

        <div v-for="p in pillars" :key="p.key" class="mb-2">
          <div class="d-flex caption"><span>{{ p.label }}</span><v-spacer />{{ detail.scores[p.key] }}</div>
          <v-progress-linear :value="detail.scores[p.key]" :color="p.color" height="8" rounded />
        </div>

        <div class="subtitle-2 font-weight-bold mt-4 mb-1">KPIs</div>
        <div v-if="!detail.kpis.length" class="caption grey--text">No KPIs in this period.</div>
        <div v-for="k in detail.kpis" :key="k.id" class="body-2 d-flex">
          <v-icon small :color="k.is_done ? 'success' : 'grey'" class="mr-1">{{ k.is_done ? 'mdi-check-circle' : 'mdi-circle-outline' }}</v-icon>
          <span>{{ k.text }}</span><v-spacer /><span class="caption">{{ k.achievement_percent }}%</span>
        </div>

        <div class="subtitle-2 font-weight-bold mt-4 mb-1">Growth areas</div>
        <div v-if="!detail.areas.length" class="caption grey--text">None set.</div>
        <div v-for="a in detail.areas" :key="a.id" class="body-2">
          <v-icon small :color="a.is_done ? 'success' : 'grey'" class="mr-1">{{ a.is_done ? 'mdi-check' : 'mdi-target' }}</v-icon>{{ a.text }}
        </div>

        <div class="subtitle-2 font-weight-bold mt-4 mb-1">Habits</div>
        <div v-if="!detail.habits.length" class="caption grey--text">No habits.</div>
        <div v-for="h in detail.habits" :key="h.id" class="mb-1">
          <div class="d-flex caption"><span>{{ h.text }} · {{ h.streak }}d streak</span><v-spacer />{{ h.consistency_pct }}%</div>
          <v-progress-linear :value="h.consistency_pct" color="orange" height="6" rounded />
        </div>

        <div class="subtitle-2 font-weight-bold mt-4 mb-1">Recognition received</div>
        <div v-if="!detail.recognition_received.length" class="caption grey--text">None this period.</div>
        <div v-for="r in detail.recognition_received" :key="r.id" class="body-2 mb-1">
          <v-chip x-small class="mr-1">{{ r.category }}</v-chip>{{ r.message }}<span class="caption grey--text"> — {{ r.from }}</span>
        </div>

        <div class="caption grey--text mt-3">
          Feedback received: {{ detail.feedback.received }} ({{ detail.feedback.anonymous }} anonymous)
        </div>

        <v-btn block color="primary" class="text-none mt-4" :loading="aiLoading" @click="review">
          <v-icon left>mdi-robot-outline</v-icon>AI review summary
        </v-btn>
        <v-alert v-if="aiError" type="warning" dense outlined class="mt-2">{{ aiError }}</v-alert>
        <div v-if="ai" class="mt-3">
          <div class="body-2 mb-2">{{ ai.summary }}</div>
          <div v-for="s in sections" :key="s.key">
            <template v-if="(ai[s.key] || []).length">
              <div class="caption font-weight-bold" :class="s.color + '--text'">{{ s.label }}</div>
              <ul class="body-2 mb-2"><li v-for="(t, i) in (ai[s.key] || [])" :key="i">{{ t }}</li></ul>
            </template>
          </div>
        </div>

        <AdminNotesSection :user-id="user.user_id" :range="range" :ai="ai" />
      </div>
    </div>
  </v-navigation-drawer>
</template>

<script>
import AdminTrendChart from './AdminTrendChart.vue'
import AdminNotesSection from './AdminNotesSection.vue'
import { getEmployee, getInsights, getTrend } from '@/services/adminGrowthService'

export default {
  name: 'AdminEmployeeDrawer',
  components: { AdminTrendChart, AdminNotesSection },
  props: { user: { type: Object, default: null }, range: { type: Object, required: true } },
  data() {
    return {
      detail: null, loading: false, error: null,
      ai: null, aiLoading: false, aiError: null,
      trend: [], trendLoading: false,
      pillars: [
        { key: 'kpi_score', label: 'KPI', color: 'primary' },
        { key: 'task_score', label: 'Tasks', color: 'teal' },
        { key: 'ai_score', label: 'AI usage', color: 'purple' },
        { key: 'habit_score', label: 'Habits', color: 'orange' },
      ],
      sections: [
        { key: 'strengths', label: 'Strengths', color: 'success' },
        { key: 'risks', label: 'Risks', color: 'error' },
        { key: 'actions', label: 'Suggested actions', color: 'primary' },
      ],
    }
  },
  watch: {
    user(u) {
      // invalidate any in-flight requests for the previous user
      this.reqId++
      this.aiReqId++
      this.trendReqId++
      this.trend = []; this.trendLoading = false
      this.ai = null; this.aiError = null; this.aiLoading = false
      this.detail = null; this.error = null; this.loading = false
      if (u) this.load()
    },
    range: {
      handler() {
        this.aiReqId++
        this.ai = null; this.aiError = null; this.aiLoading = false
        if (this.user) this.load()
      },
      deep: true,
    },
  },
  created() {
    // non-reactive request counters (guard out-of-order responses)
    this.reqId = 0
    this.aiReqId = 0
    this.trendReqId = 0
  },
  methods: {
    async loadTrend() {
      const id = ++this.trendReqId
      this.trendLoading = true
      try {
        const data = await getTrend(this.range.from, this.range.to, this.user.user_id)
        if (id === this.trendReqId) this.trend = data.buckets || []
      } catch (e) {
        if (id === this.trendReqId) this.trend = []
      } finally {
        if (id === this.trendReqId) this.trendLoading = false
      }
    },
    async load() {
      this.loadTrend()
      const id = ++this.reqId
      this.loading = true; this.error = null
      try {
        const data = await getEmployee(this.user.user_id, this.range.from, this.range.to)
        if (id !== this.reqId) return
        this.detail = data
      } catch (e) {
        if (id !== this.reqId) return
        this.error = e.message
      } finally {
        if (id === this.reqId) this.loading = false
      }
    },
    async review() {
      const id = ++this.aiReqId
      this.aiLoading = true; this.aiError = null
      try {
        const data = await getInsights({ from: this.range.from, to: this.range.to, user_id: this.user.user_id })
        if (id !== this.aiReqId) return
        this.ai = data
      } catch (e) {
        if (id !== this.aiReqId) return
        this.aiError = 'Insights unavailable. ' + e.message
      } finally {
        if (id === this.aiReqId) this.aiLoading = false
      }
    },
  },
}
</script>
